// Remote Support realtime client. One instance per editor session; connects
// to the API's /api/collab/ws socket, authenticates with the app session
// token, joins a room (owner or agent), and shuttles ops/cursors/state.
//
// Reconnect behavior: the owner reconnects forever with backoff (their room
// is recreated on rejoin). An agent silently retries for ~30s — that covers
// an owner page refresh — before surfacing "session ended" to the UI.

const { getApiBasePath, getAccessToken } = require('./apiClient');

var CURSOR_THROTTLE_MS = 30;
var AGENT_RETRY_WINDOW_MS = 30000;
var RETRY_DELAY_MS = 2500;

function getCollabWsUrl() {
  var base = getApiBasePath(); // absolute ("https://host/api") or relative ("/api")
  if (!/^https?:/i.test(base)) {
    base = window.location.origin + base;
  }
  return base.replace(/^http/i, 'ws') + '/collab/ws';
}

// handlers: { onJoined, onRoster, onOps, onCursor, onStateRequest, onState,
//             onSaved, onSessionEnded, onJoinDenied, onKicked, onStatus }
// All optional. join params: { role: 'owner'|'agent', workflowId, ownerId?,
// workflowName? }.
function createCollabClient(handlers) {
  var ws = null;
  var joinParams = null;
  var closed = true;          // true after disconnect() — stops reconnects
  var joinedRoom = false;
  var retryTimer = null;
  var retryDeadline = 0;
  var lastCursorSentAt = 0;
  var cursorTrailTimer = null;
  var pendingCursor = null;

  function emit(name, payload) {
    if (handlers && typeof handlers[name] === 'function') handlers[name](payload);
  }

  function send(msg) {
    if (!ws || ws.readyState !== 1) return false;
    try { ws.send(JSON.stringify(msg)); return true; } catch (_) { return false; }
  }

  function openSocket() {
    var socket = new WebSocket(getCollabWsUrl());
    ws = socket;
    socket.onopen = function() {
      send({ type: 'auth', token: getAccessToken() });
    };
    socket.onmessage = function(event) {
      var msg = null;
      try { msg = JSON.parse(event.data); } catch (_) {}
      if (!msg || socket !== ws) return;
      handleMessage(msg);
    };
    socket.onclose = function() {
      if (socket !== ws) return;
      joinedRoom = false;
      emit('onStatus', 'disconnected');
      scheduleRetry();
    };
    socket.onerror = function() {};
  }

  function handleMessage(msg) {
    if (msg.type === 'auth_result') {
      if (!msg.ok) {
        closed = true;
        emit('onStatus', 'auth_failed');
        return;
      }
      if (joinParams) send(Object.assign({ type: 'join' }, joinParams));
      return;
    }
    if (msg.type === 'joined') {
      joinedRoom = true;
      retryDeadline = 0;
      emit('onStatus', 'connected');
      emit('onJoined', msg);
      return;
    }
    if (msg.type === 'join_denied') {
      if (msg.reason === 'not_live' && joinParams && joinParams.role === 'agent') {
        // Owner may just be refreshing — keep quietly retrying for a bit.
        scheduleRetry();
        return;
      }
      closed = true;
      emit('onJoinDenied', msg);
      return;
    }
    if (msg.type === 'session_ended') {
      joinedRoom = false;
      scheduleRetry();
      return;
    }
    if (msg.type === 'kicked') {
      closed = true;
      emit('onKicked', msg);
      return;
    }
    if (msg.type === 'exec_event' || msg.type === 'selection' || msg.type === 'ui_state') {
      return emit('onPeerEvent', msg);
    }
    if (msg.type === 'mirror_event') return emit('onMirrorEvent', msg);
    if (msg.type === 'mirror_resync') return emit('onMirrorResync', msg);
    if (msg.type === 'pointer') return emit('onPointer', msg);
    if (msg.type === 'roster') return emit('onRoster', msg);
    if (msg.type === 'ops') return emit('onOps', msg);
    if (msg.type === 'cursor') return emit('onCursor', msg);
    if (msg.type === 'state_request') return emit('onStateRequest', msg);
    if (msg.type === 'state') return emit('onState', msg);
    if (msg.type === 'saved') return emit('onSaved', msg);
  }

  // Owner: retry forever (their reconnect recreates the room). Agent: retry
  // inside a 30s window, then give up and tell the UI the session ended.
  function scheduleRetry() {
    if (closed || retryTimer) return;
    var isAgent = joinParams && joinParams.role === 'agent';
    if (isAgent) {
      if (!retryDeadline) retryDeadline = Date.now() + AGENT_RETRY_WINDOW_MS;
      if (Date.now() > retryDeadline) {
        closed = true;
        emit('onSessionEnded', {});
        return;
      }
    }
    retryTimer = setTimeout(function() {
      retryTimer = null;
      if (closed) return;
      if (ws && ws.readyState === 1) {
        // Socket survived (e.g. session_ended after an owner refresh) —
        // just try to join again on the same connection.
        send(Object.assign({ type: 'join' }, joinParams));
        scheduleRetry();
        return;
      }
      openSocket();
    }, RETRY_DELAY_MS);
  }

  return {
    connect: function(params) {
      joinParams = params;
      closed = false;
      retryDeadline = 0;
      openSocket();
    },
    disconnect: function() {
      closed = true;
      joinParams = null;
      joinedRoom = false;
      if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
      if (cursorTrailTimer) { clearTimeout(cursorTrailTimer); cursorTrailTimer = null; }
      var socket = ws;
      ws = null;
      if (socket) { try { socket.close(1000); } catch (_) {} }
    },
    isInSession: function() {
      return joinedRoom && !!ws && ws.readyState === 1;
    },
    sendOps: function(ops) {
      if (!ops || !ops.length) return;
      send({ type: 'ops', ops: ops });
    },
    // Throttled (leading + trailing) so mousemove storms stay smooth without
    // flooding the socket.
    sendCursor: function(x, y) {
      pendingCursor = { x: x, y: y };
      var now = Date.now();
      if (now - lastCursorSentAt >= CURSOR_THROTTLE_MS) {
        lastCursorSentAt = now;
        send({ type: 'cursor', x: x, y: y });
        pendingCursor = null;
        return;
      }
      if (cursorTrailTimer) return;
      cursorTrailTimer = setTimeout(function() {
        cursorTrailTimer = null;
        if (!pendingCursor) return;
        lastCursorSentAt = Date.now();
        send({ type: 'cursor', x: pendingCursor.x, y: pendingCursor.y });
        pendingCursor = null;
      }, CURSOR_THROTTLE_MS);
    },
    // Owner answers an agent's join bootstrap.
    sendState: function(forSession, state, workflowName) {
      send({ type: 'state_response', forSession: forSession, state: state, workflowName: workflowName });
    },
    sendSaved: function(updatedAt) {
      send({ type: 'saved', updatedAt: updatedAt });
    },
    // Generic peer event (exec_event | selection | ui_state) — relayed to the
    // other room members with sender identity attached by the server.
    sendPeerEvent: function(type, payload) {
      send({ type: type, payload: payload });
    },
    // Owner -> agents: one rrweb DOM event of the live screen mirror.
    // NOTE: `type` must stay the FIRST key — the server relays mirror
    // frames verbatim off a byte-prefix check without parsing them.
    sendMirrorEvent: function(event) {
      send({ type: 'mirror_event', payload: event });
    },
    // Agent -> owner: my mirror went silent/stale, take a fresh snapshot.
    sendMirrorResync: function() {
      send({ type: 'mirror_resync' });
    },
    // Agent -> owner: virtual pointer input (move/click/wheel/key…).
    sendPointer: function(payload) {
      send({ type: 'pointer', payload: payload });
    }
  };
}

module.exports = { createCollabClient };
